import React from 'react';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import { TypographyType, TypographyVariant } from '../../../../../../../components-dummy';
import { ColorStyle } from './ColorStyle';
import { TextFieldWithAdornment } from './TextFieldWithAdornment';
import { DropdownStyle } from './DropdownStyle';
import { fonts } from '../../../../../constants/fonts';
import { defaultFontFamily } from '../../../../../constants/default-font-family';
import { defaultFontSize } from '../../../../../constants/default-font-size';
import { defaultLineHeight } from '../../../../../constants/default-line-height';
import { minFontSize } from '../../../../../constants/min-font-size';
import { maxFontSize } from '../../../../../constants/max-font-size';
import { minLineHeight } from '../../../../../constants/min-line-height';
import { maxLineHeight } from '../../../../../constants/max-line-height';
import { ConfigType } from '../../../../../types/widget-maker';
import {
  ToggleButtonStyled,
  FontStyle,
  FontStyleRow,
  ToggleButtonGroupStyled,
  ErrorLabelStyled,
} from './EmailDisplaySettingsSection.styles';

interface Props {
  config: ConfigType;
  onChange: (field: string, value: any, index?: number) => void;
  index?: number;
  getErrorMessage: (key: string) => string;
}

export const FontStyles = ({ config, onChange, index, getErrorMessage }: Props) => {
  const handleFontWeightChange = () =>
    onChange('fontWeight', config?.fontWeight === 'bold' ? 'normal' : 'bold', index);
  const handleFontStyleChange = () =>
    onChange('fontStyle', config?.fontStyle === 'italic' ? 'normal' : 'italic', index);
  const handleTextDecorationChange = () =>
    onChange(
      'textDecoration',
      config?.textDecoration === 'underline' ? 'none' : 'underline',
      index
    );
  const handleTextAlignmentChange = (e, newAlignment) => onChange('textAlign', newAlignment, index);

  const fontSizeError = getErrorMessage('fontSize');
  const lineHeightError = getErrorMessage('lineHeight');

  return (
    <>
      <FontStyleRow>
        <FontStyle width={62}>
          <DropdownStyle
            data={fonts}
            field='fontFamily'
            defaultValue={defaultFontFamily}
            value={config?.fontFamily}
            onChange={(field, value) => onChange(field, value, index)}
            label='Font Family'
          />
        </FontStyle>
        <FontStyle width={37}>
          <TextFieldWithAdornment
            field='fontSize'
            label=''
            defaultValue={defaultFontSize}
            value={config?.fontSize}
            adornment='px'
            onChange={(field, value) => onChange(field, value, index)}
            inputProps={{
              min: minFontSize,
              max: maxFontSize,
            }}
            error={!!fontSizeError}
          />
        </FontStyle>
      </FontStyleRow>
      {fontSizeError && (
        <ErrorLabelStyled type={TypographyType.Paragraph} variant={TypographyVariant.SmallRegular}>
          {fontSizeError}
        </ErrorLabelStyled>
      )}
      <FontStyleRow>
        <FontStyle width={30}>
          <TextFieldWithAdornment
            field='lineHeight'
            label='Line height'
            defaultValue={defaultLineHeight}
            value={config?.lineHeight}
            adornment='%'
            onChange={(field, value) => onChange(field, value, index)}
            inputProps={{
              min: minLineHeight,
              max: maxLineHeight,
              step: 10,
            }}
            error={!!lineHeightError}
          />
        </FontStyle>
        <FontStyle width={30}>
          <TextFieldWithAdornment
            field='letterSpacing'
            label='Letter spacing'
            defaultValue={0}
            value={config?.letterSpacing}
            adornment='px'
            onChange={(field, value) => onChange(field, value, index)}
            inputProps={{
              step: 0.1,
            }}
          />
        </FontStyle>
        <ToggleButtonGroupStyled
          value={config?.textAlign || 'center'}
          exclusive
          onChange={handleTextAlignmentChange}
          aria-label='text alignment'
          className='text-alignment'
        >
          <ToggleButtonStyled value='left' aria-label='left aligned'>
            <FormatAlignLeftIcon />
          </ToggleButtonStyled>
          <ToggleButtonStyled value='center' aria-label='center aligned'>
            <FormatAlignCenterIcon />
          </ToggleButtonStyled>
          <ToggleButtonStyled value='right' aria-label='right aligned'>
            <FormatAlignRightIcon />
          </ToggleButtonStyled>
        </ToggleButtonGroupStyled>
      </FontStyleRow>
      {lineHeightError && (
        <ErrorLabelStyled type={TypographyType.Paragraph} variant={TypographyVariant.SmallRegular}>
          {lineHeightError}
        </ErrorLabelStyled>
      )}
      <FontStyleRow>
        <ColorStyle
          field='textColor'
          label='Text color'
          defaultColor='#000000'
          color={config?.textColor}
          onChange={(field, value) => onChange(field, value, index)}
        />
        <ToggleButtonGroupStyled>
          <ToggleButtonStyled
            value={config?.fontWeight}
            aria-label='bold'
            onClick={handleFontWeightChange}
            selected={config?.fontWeight === 'bold'}
          >
            <FormatBoldIcon />
          </ToggleButtonStyled>
          <ToggleButtonStyled
            value={config?.fontStyle}
            aria-label='italic'
            onClick={handleFontStyleChange}
            selected={config?.fontStyle === 'italic'}
          >
            <FormatItalicIcon />
          </ToggleButtonStyled>
          <ToggleButtonStyled
            value={config?.textDecoration}
            aria-label='underlined'
            onClick={handleTextDecorationChange}
            selected={config?.textDecoration === 'underline'}
          >
            <FormatUnderlinedIcon />
          </ToggleButtonStyled>
        </ToggleButtonGroupStyled>
      </FontStyleRow>
    </>
  );
};
